import React, { useCallback, useEffect, useRef, useState } from 'react';
import AccountOverview from '../components/Account/AccountOverview';
import SubscriptionStatus from '../components/Account/SubscriptionStatus';
import AccountStats from '../components/Account/AccountStats';
import CurrentSubscription from '../components/Account/CurrentSubscription';
import AvailablePlans from '../components/Account/AvailablePlans';
// PaymentMethodsModal is opened via openModal, not directly imported for rendering here.
import PaymentHistory from '../components/Account/PaymentHistory';
import UpgradeBanner from '../components/Account/UpgradeBanner';
import PageTransition from '../components/Shared/PageTransition';
import { useAuth } from '../contexts/AuthContext';
import useWebSocket from '../hooks/useWebSocket';
import { WS_ACCOUNT_TYPES, getAccountAppEndpoint, getAccountUserQueueTopic, WS_ACCOUNT_TOPICS, WS_ACCOUNT_USER_TOPICS } from '../config/wsAccountConfig';
import LoadingSkeleton from '../components/Shared/LoadingSkeleton';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';

// Mock payment methods for demonstration
const MOCK_PAYMENT_METHODS = [
  { id: 'stripe_card_mock', name: 'Credit/Debit Card', description: 'Securely with Stripe', icon: 'fas fa-credit-card', iconColor: 'text-blue-400' },
  { id: 'paypal_mock', name: 'PayPal', description: 'Use your PayPal account', icon: 'fab fa-paypal', iconColor: 'text-blue-600' },
  { id: 'crypto_mock', name: 'Cryptocurrency', description: 'BTC, ETH, LTC accepted', icon: 'fab fa-bitcoin', iconColor: 'text-yellow-500' },
  { ids: 'sbp_mock', name: 'SBP', description: 'Fast bank transfers', icon: 'fa-money-bill-wave', iconColor: 'text-accent-green' }, // Ensure 'id' is present
 ];

const PLAN_DETAILS_MAP = {
    Free: { icon: 'fa-cube', iconBgColorNonCurrent: 'bg-accent-free/20', iconTextColorNonCurrent: 'text-accent-free', cardGradientClass: 'gradient-bg-free' },
    Vip: { icon: 'fa-star', iconBgColorNonCurrent: 'bg-accent-yellow/20', iconTextColorNonCurrent: 'text-accent-yellow', cardGradientClass: 'gradient-bg-yellow' }, // Changed "VIP" to "Vip"
    Premium: { icon: 'fa-gem', iconBgColorNonCurrent: 'bg-accent-purple/20', iconTextColorNonCurrent: 'text-accent-purple', cardGradientClass: 'gradient-bg-purple' },
    Professional: { icon: 'fa-crown', iconBgColorNonCurrent: 'bg-accent-pink/20', iconTextColorNonCurrent: 'text-accent-pink', cardGradientClass: 'gradient-bg-pink' },
};

const AccountPage = ({ openModal, closeModal }) => {
    const { user, isAuthenticated, isAuthLoading } = useAuth();
    const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
    const location = useLocation();
    const navigate = useNavigate();

    const [accountState, setAccountState] = useState({
        userData: null,
        subscription: null,
        stats: null,
        paymentHistory: [],
        availablePlans: [],
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isProcessingAction, setIsProcessingAction] = useState(false);
    const [error, setError] = useState(null);

    const [pendingModalAction, setPendingModalAction] = useState(null);
    // Structure: { type: 'upgrade' | 'extend', plan: PlanDto, paymentMethod?: object, phase?: string, paymentDetails?: object }

    const subscriptions = useRef({ state: null, status: null, error: null });

    useEffect(() => {
        if (location.state?.scrollTo === "plans") {
            const element = document.getElementById("available-plans-section");
            const timer = setTimeout(() => {
                element?.scrollIntoView({ behavior: "smooth", block: "center" });
                navigate(location.pathname, { replace: true, state: {} });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [location.state, location.pathname, navigate]);

    const mergeAccountState = useCallback((prevState, newServerState) => {
        const updateData = newServerState.data || newServerState;

        const mergedUserData = {
            ...(prevState?.userData || {}),
            ...(updateData.userData || {}),
            name: user?.name || updateData.userData?.name || prevState?.userData?.name || 'User',
             email: user?.email || updateData.userData?.email || prevState?.userData?.email,
         };
 
        const currentPlanType = updateData.subscription?.type ?? prevState?.subscription?.type ?? 'Free'; // Default to Free if null
        const plansSource = Array.isArray(updateData.availablePlans) ? updateData.availablePlans : (Array.isArray(prevState?.availablePlans) ? prevState.availablePlans : []);
        
        const updatedPlans = plansSource.map(plan => {
            const planSpecificDetails = PLAN_DETAILS_MAP[plan.name] || { icon: 'fa-question-circle', iconBgColorNonCurrent: 'bg-gray-500/20', iconTextColorNonCurrent: 'text-gray-400', cardGradientClass: '' };
            const isCurrentPlan = plan.name === currentPlanType;

            return {
                ...plan,
                icon: planSpecificDetails.icon,
                iconBgColor: isCurrentPlan && plan.name !== 'Free' ? 'bg-gradient-to-br from-accent-purple to-accent-blue' : planSpecificDetails.iconBgColorNonCurrent,
                iconTextColor: isCurrentPlan && plan.name !== 'Free' ? 'text-white' : planSpecificDetails.iconTextColorNonCurrent,
                applyPulseToIcon: isCurrentPlan && plan.name !== 'Free',
                cardGradientClass: !isCurrentPlan ? planSpecificDetails.cardGradientClass : '',
                isCurrent: isCurrentPlan,
            };
        });

        return {
            userData: mergedUserData,
            subscription: updateData.subscription !== undefined ? updateData.subscription : prevState?.subscription,
            stats: updateData.stats ?? prevState?.stats,
            paymentHistory: updateData.paymentHistory ?? prevState?.paymentHistory,
            availablePlans: updatedPlans,
        };
    }, [user]);

    const handlePaymentMethodSubmission = useCallback((submittedPlan, submittedActionType, selectedMethod) => {
        // Enhanced logging for diagnostics
        console.log('[AccountPage] handlePaymentMethodSubmission: Entered function.');
        console.log('[AccountPage] handlePaymentMethodSubmission: submittedPlan:', JSON.stringify(submittedPlan));
        console.log('[AccountPage] handlePaymentMethodSubmission: submittedActionType:', submittedActionType);
        console.log('[AccountPage] handlePaymentMethodSubmission: selectedMethod (raw):', JSON.stringify(selectedMethod));

        setIsProcessingAction(true);
        
        let paymentMethodIdToSend = null;
        console.log("Type: ", typeof selectedMethod);
        if (selectedMethod && typeof selectedMethod === 'object' && Object.prototype.hasOwnProperty.call(selectedMethod, 'id')) {
            paymentMethodIdToSend = selectedMethod.id;
            console.log(`[AccountPage] handlePaymentMethodSubmission: Extracted ID "${paymentMethodIdToSend}" from selectedMethod.`);
        } else {
            console.error('[AccountPage] handlePaymentMethodSubmission: CRITICAL - selectedMethod is invalid or "id" property is missing. selectedMethod:', selectedMethod);
            // Optionally, set an error to UI and abort:
            // setError("Could not process payment method. Please try again.");
            // setIsProcessingAction(false);
            // return;
        }
        
        // Update pendingModalAction after extracting necessary data, to avoid using stale data if this callback is somehow mismanaged.
        setPendingModalAction(prev => ({
            ...prev,
            plan: submittedPlan,
            type: submittedActionType,
            paymentMethod: selectedMethod, // Store the original selectedMethod object for modal state
            phase: 'awaiting_url'
        }));

        const topic = submittedActionType === 'upgrade' ? WS_ACCOUNT_TOPICS.UPGRADE : WS_ACCOUNT_TOPICS.EXTEND;
        const payload = {
            planId: submittedPlan ? String(submittedPlan.name) : null, // Ensure planId is a string
            paymentMethodId: paymentMethodIdToSend // Use the carefully extracted/validated ID
        };

        console.log('[AccountPage] handlePaymentMethodSubmission: Sending payload:', JSON.stringify(payload), 'to topic endpoint for:', topic);
        sendMessage(getAccountAppEndpoint(topic), payload);
    }, [sendMessage, setIsProcessingAction, setPendingModalAction, getAccountAppEndpoint, WS_ACCOUNT_TOPICS.UPGRADE, WS_ACCOUNT_TOPICS.EXTEND]); // Added missing dependencies


    const handleStateUpdate = useCallback((message) => {
        console.log('%cWS Account State/Update Received:', 'color: green;', message);
        setError(null);
        setIsLoading(false);
        if (!pendingModalAction) {
            setIsProcessingAction(false);
        }
        setAccountState(prev => mergeAccountState(prev, message));
    }, [mergeAccountState, pendingModalAction, setIsLoading, setIsProcessingAction, setAccountState, setError]); // Added all state setters

    const handleStatusUpdate = useCallback((message) => {
        console.log('%cWS Account Status Received:', 'color: blue;', message);
        setError(null);
        const { type: messageType, data: actionData } = message; 

        switch (messageType) {
            case WS_ACCOUNT_TYPES.ACTION_INITIATED:
if (actionData?.paymentDetails?.paymentUrl && pendingModalAction?.plan) {
                    console.log("Payment URL received:", actionData.paymentDetails.paymentUrl, "for plan:", pendingModalAction.plan.name);
                    setPendingModalAction(prev => ({ ...prev, phase: 'url_received', paymentDetails: actionData.paymentDetails }));
                    // REMOVE openModal call from here; useEffect on pendingModalAction will handle it.
                    // openModal('payment', {
                    //     plan: pendingModalAction.plan,
                    //     actionType: pendingModalAction.type,
                    //     // methods: MOCK_PAYMENT_METHODS, // No longer pass methods from here
                    //     paymentDetails: actionData.paymentDetails,
                    //     onConfirmWithMethod: handlePaymentMethodSubmission,
                    // });
                } else if (actionData?.action === "cancel") {
                    console.log("Cancel action initiated (no payment details expected). Awaiting final status.");
                } else {
                    console.warn(
                        "Action initiated but no payment URL or unknown action type. WS Message (payload):", actionData,
                        "Pending Action State:", pendingModalAction
                    );
                    setIsProcessingAction(false);
                    setPendingModalAction(null);
                    if (typeof closeModal === 'function') closeModal('payment'); // Ensure closeModal is a function before calling
                    setError(actionData?.message || "Could not process the payment initiation. Please try again.");
                }
                break;
case WS_ACCOUNT_TYPES.ACTION_SUCCESS:
                setIsProcessingAction(false); // Always stop spinner

                if (actionData?.action === pendingModalAction?.type &&
                    (actionData.action === 'upgrade' || actionData.action === 'extend')) {
                    // This success message is for the payment action initiated via the modal
                    console.log(`Payment action '${actionData.action}' success for plan '${pendingModalAction.plan.name}'. Modal will be updated.`);
                    setPendingModalAction(prev => ({
                        ...prev,
                        phase: 'completed_successfully', // New phase for modal to pick up
                        statusMessage: actionData.message, // Message from backend for modal
                        paymentDetails: null, // Explicitly clear paymentDetails from pending state
                    }));
                    // The useEffect hook observing pendingModalAction will call openModal with new props.
                    // DO NOT call closeModal('payment') here; the modal will handle its success view and closure.
                } else if (actionData?.action === "cancel") {
                    console.log("Cancel action succeeded.");
                    closeModal('payment'); // Close payment modal if it was somehow open
                    setPendingModalAction(null); 
                } else {
                    // Other generic success, or if no modal was pending for this action
                    console.log(`Generic action '${actionData?.action}' success.`);
                    closeModal('payment');
                    setPendingModalAction(null);
                }

                // Update account state for ALL success types that provide it
                if (actionData?.updatedState) {
                    setAccountState(prev => mergeAccountState(prev, actionData.updatedState));
                } else {
                    console.log("ACTION_SUCCESS without updatedState, requesting full refresh.");
                    if (isWebSocketConnected) {
                        sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.GET_STATE), {});
                    }
                }
                break;
            case WS_ACCOUNT_TYPES.ACTION_FAILED:
                setIsProcessingAction(false);
                setPendingModalAction(null);
                setError(actionData?.message || 'An unknown error occurred during the action.');
                console.error(`Action failed: ${actionData?.action}`, message);
                break;
            default:
                console.warn('Unhandled account status message type:', messageType, message);
        }
    }, [isWebSocketConnected, openModal, closeModal, sendMessage, mergeAccountState, pendingModalAction, handlePaymentMethodSubmission, setError, setIsProcessingAction, setPendingModalAction, setAccountState]);  // Added all relevant state setters

    const handleErrorUpdate = useCallback((message) => {
        console.error('%cWS Account Error Received:', 'color: red;', message);
        setError(message?.message || message?.error || 'An unknown error occurred.');
        setIsLoading(false);
        setIsProcessingAction(false);
        setPendingModalAction(null);
        if (typeof closeModal === 'function') {
            closeModal('payment');
        } else {
            console.error("[AccountPage] handleErrorUpdate: closeModal is not a function. closeModal value:", closeModal);
        }
    }, [closeModal, setError, setIsLoading, setIsProcessingAction, setPendingModalAction]); // Added relevant state setters

    const handleStateUpdateRef = useRef(handleStateUpdate);
    const handleStatusUpdateRef = useRef(handleStatusUpdate);
    const handleErrorUpdateRef = useRef(handleErrorUpdate);

    useEffect(() => { handleStateUpdateRef.current = handleStateUpdate; }, [handleStateUpdate]);
    useEffect(() => { handleStatusUpdateRef.current = handleStatusUpdate; }, [handleStatusUpdate]);
    useEffect(() => { handleErrorUpdateRef.current = handleErrorUpdate; }, [handleErrorUpdate]);

     useEffect(() => {
        if (isAuthLoading || !isAuthenticated) {
            setIsLoading(true);
            setAccountState({ userData: null, subscription: null, stats: null, paymentHistory: [], availablePlans: [] });
            if (subscriptions.current.state) Object.values(subscriptions.current).forEach(unsub => unsub?.());
            subscriptions.current = { state: null, status: null, error: null };
            if (!isAuthLoading && !isAuthenticated) setError("Please log in to view account details.");
            return;
        }

        if (isWebSocketConnected) {
            setIsLoading(true);
            setError(null);
            console.log("Subscribing to Account WebSocket topics...");

            const unsubState = subscribe(getAccountUserQueueTopic(WS_ACCOUNT_USER_TOPICS.STATE), (msg) => handleStateUpdateRef.current(msg));
            const unsubStatus = subscribe(getAccountUserQueueTopic(WS_ACCOUNT_USER_TOPICS.STATUS), (msg) => handleStatusUpdateRef.current(msg));
            const unsubError = subscribe(getAccountUserQueueTopic(WS_ACCOUNT_USER_TOPICS.ERROR), (msg) => handleErrorUpdateRef.current(msg));
            subscriptions.current = { state: unsubState, status: unsubStatus, error: unsubError };

            const timerId = setTimeout(() => {
                if (isWebSocketConnected && isAuthenticated) {
                    console.log("Requesting current Account state via WebSocket...");
                    sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.GET_STATE), {});
                }
            }, 150);

            return () => {
                console.log("Unsubscribing from Account WebSocket topics...");
                unsubState?.();
                unsubStatus?.();
                unsubError?.();
                subscriptions.current = { state: null, status: null, error: null };
                clearTimeout(timerId);
            };
        } else {
            setIsLoading(true);
            setError("Connecting to server...");
        }
    }, [isWebSocketConnected, isAuthenticated, isAuthLoading, subscribe, sendMessage]);

    // Effect to manage opening/updating the payment modal based on pendingModalAction state
    useEffect(() => {
        if (!pendingModalAction) {
            // If pending action is cleared (e.g. by modal's onClose), ensure modal is closed.
            // closeModal('payment'); // This line might be redundant if onClose always clears pendingModalAction.
            return;
        }

        const { phase, plan, type, paymentDetails: pdaPaymentDetails, statusMessage: pdaStatusMessage } = pendingModalAction;

        let modalProps = {
            plan: plan,
            actionType: type,
            onConfirmWithMethod: handlePaymentMethodSubmission,
            onClose: () => {
                closeModal('payment');
                setPendingModalAction(null); // Crucial: Reset pending action when modal is closed
            }
        };

        switch (phase) {
            case 'completed_successfully':
                modalProps.initialPhase = 'payment_successful';
                modalProps.statusMessage = pdaStatusMessage;
                modalProps.paymentDetails = null; // Ensure it's null for this phase
                break;
            case 'completed_with_error': // Assuming a similar error phase might exist or be added
                modalProps.initialPhase = 'payment_error';
                modalProps.statusMessage = pdaStatusMessage; // Or a generic error message
                modalProps.paymentDetails = null;
                break;
            case 'url_received':
                modalProps.paymentDetails = pdaPaymentDetails;
                if (pdaPaymentDetails?.paymentUrl) {
                    modalProps.initialPhase = 'awaiting_payment_completion';
                } else {
                    modalProps.initialPhase = 'payment_error';
                    modalProps.statusMessage = pdaPaymentDetails?.message || "Failed to process payment initiation. Please try again.";
                }
                break;
            case 'awaiting_url':
                modalProps.initialPhase = 'initiating_payment';
                // paymentDetails might be undefined or null here, passed as is
                modalProps.paymentDetails = pdaPaymentDetails;
                break;
            case 'method_selection':
                // initialPhase remains undefined, modal will default to selecting method
                modalProps.paymentDetails = null; // Ensure no stale payment details
                break;
            default:
                // If phase is unknown or doesn't require modal update, do nothing or log warning.
                // For safety, we might not want to call openModal if phase is unrecognized.
                console.warn("[AccountPage Effect] Unhandled pendingModalAction phase for modalProps construction:", phase);
                return; // Don't proceed to openModal if phase isn't explicitly handled for it
        }

        // Ensure openModal is only called for phases that are meant to interact with the payment modal
        if (phase === 'method_selection' || phase === 'awaiting_url' || phase === 'url_received' || phase === 'completed_successfully' || phase === 'completed_with_error') {
            console.log("[AccountPage Effect] Opening/Updating payment modal. Phase:", phase, "Generated Props:", JSON.parse(JSON.stringify(modalProps))); // Deep log
            openModal('payment', modalProps);
        }
    }, [pendingModalAction, openModal, closeModal, handlePaymentMethodSubmission]); // Add all dependencies

    const handleSelectPlan = useCallback((plan) => {
        if (isProcessingAction || !plan) return;
        console.log("Upgrade selected for plan:", plan.name, plan.id);
        setError(null);
        setPendingModalAction({ type: 'upgrade', plan: plan, phase: 'method_selection' });
        // useEffect above will call openModal
    }, [isProcessingAction]); // Removed openModal, handlePaymentMethodSubmission from deps as useEffect handles it

    const handleExtendSubscription = useCallback(() => {
        if (isProcessingAction || !accountState.subscription || accountState.subscription.type === 'Free') return;
        const currentPlanDetails = accountState.availablePlans.find(p => p.name === accountState.subscription?.type);
        if (!currentPlanDetails) {
            setError("Could not find details for the current plan to extend.");
            return;
        }
        console.log("Extending subscription for:", currentPlanDetails.name);
        setError(null);
        const planForModal = { ...currentPlanDetails, isCurrent: true };
        setPendingModalAction({ type: 'extend', plan: planForModal, phase: 'method_selection' });
        // useEffect above will call openModal
    }, [isProcessingAction, accountState.subscription, accountState.availablePlans ]); // Removed openModal, handlePaymentMethodSubmission

    const handleCancelSubscription = useCallback(() => {
        if (isProcessingAction || !accountState.subscription || accountState.subscription.type === 'Free') return;
        console.log("Cancelling subscription for:", accountState.subscription.type);
        setError(null);
        setIsProcessingAction(true);
        const planForAction = accountState.availablePlans.find(p => p.name === accountState.subscription?.type) || { name: accountState.subscription.type };
        setPendingModalAction({ type: 'cancel', plan: planForAction });
        sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.CANCEL), {});
    }, [isProcessingAction, accountState.subscription, accountState.availablePlans, sendMessage]);

    const handleChangePlan = useCallback(() => {
        document.getElementById('available-plans-section')?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    if (isLoading && !accountState.userData) { 
        return (
            <PageTransition><div className="p-6 lg:p-8 space-y-8">
                <LoadingSkeleton count={1} className="h-40 rounded-2xl" />
                <LoadingSkeleton count={1} className="h-20 rounded-2xl" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">{[...Array(4)].map((_, i) => <LoadingSkeleton key={i} count={1} className="h-24 rounded-2xl" />)}</div>
                <LoadingSkeleton count={1} className="h-60 rounded-2xl" /><LoadingSkeleton count={1} className="h-80 rounded-2xl" /><LoadingSkeleton count={1} className="h-40 rounded-2xl" />
            </div></PageTransition>
        );
    }

    if (!isAuthLoading && !isAuthenticated && error) { 
         return <PageTransition><div className="p-6 lg:p-8 text-center text-accent-red"><i className="fas fa-exclamation-triangle fa-3x mb-4"></i><p>{error}</p></div></PageTransition>;
    }

    if (!isLoading && !accountState.userData && !error) {
        return <PageTransition><div className="p-6 lg:p-8 text-center text-accent-red"><i className="fas fa-exclamation-triangle fa-3x mb-4"></i><p>Could not load account data. Please try refreshing.</p></div></PageTransition>;
    }

    return (
        <PageTransition>
            <div className="p-6 lg:p-8 relative">
                <AnimatePresence>
                    {error && !isLoading && ( 
                        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
                            className="fixed top-24 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg text-sm z-50 flex items-center">
                            <i className="fas fa-exclamation-circle mr-2"></i><span>{error}</span>
                            <button onClick={() => setError(null)} className="ml-4 font-bold hover:text-red-200 text-lg leading-none">×</button>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {isProcessingAction && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-dark-800/60 backdrop-blur-sm flex items-center justify-center z-40 rounded-2xl">
                            <i className="fas fa-spinner fa-spin text-accent-purple text-4xl"></i>
                            <span className="ml-4 text-white text-lg font-medium">Processing...</span>
                        </motion.div>
                    )}
                </AnimatePresence>

                {accountState.userData && accountState.subscription && (
                    <AccountOverview
                        userData={{ ...accountState.userData, email: user?.email, subscription: accountState.subscription }} 
                        onManageSubscription={handleChangePlan}
                    />
                )}
                {accountState.subscription && <SubscriptionStatus subscription={accountState.subscription} />}
                {accountState.stats && <AccountStats stats={accountState.stats} />}

                {accountState.subscription && accountState.subscription.type !== 'Free' && (
                    <CurrentSubscription
                        subscription={accountState.subscription}
                        onChangePlan={handleChangePlan}
                        onExtend={handleExtendSubscription}
                        onCancel={handleCancelSubscription} 
                        isProcessing={isProcessingAction} 
                    />
                )}

                <div id="available-plans-section">
                    <AvailablePlans
                        plans={accountState.availablePlans || []}
                        onSelectPlan={handleSelectPlan}
                        isLoading={isProcessingAction}
                    />
                </div>

                <PaymentHistory payments={accountState.paymentHistory || []} />

                {accountState.subscription?.type !== 'Professional' && accountState.availablePlans.find(p => p.name === 'Professional') && (
                    <UpgradeBanner
                        onUpgradeClick={() => handleSelectPlan(accountState.availablePlans.find(p => p.name === 'Professional'))}
                    />
                )}
            </div>
        </PageTransition>
    );
};

export default AccountPage;